import React, { useState, useEffect } from 'react';
import { 
  Database, 
  FileSpreadsheet, 
  Video, 
  FileText, 
  CheckSquare, 
  RefreshCw, 
  ExternalLink, 
  Plus, 
  CheckCircle2, 
  Sparkles, 
  Layers, 
  ShieldCheck, 
  LogIn, 
  LogOut, 
  User as UserIcon,
  Copy,
  Send,
  AlertCircle
} from 'lucide-react';
import { auth, loginWithGoogle, logoutUser, getGoogleAccessToken, User } from '../lib/firebase.ts';
import { onAuthStateChanged } from 'firebase/auth';

interface WorkspaceItem {
  id: number;
  hubId: string;
  title: string;
  content: string | null;
  category: string | null;
  externalId: string | null;
  externalUrl: string | null;
  isCompleted: boolean;
  createdAt: string;
}

interface GoogleTask {
  id: string;
  title: string;
  notes?: string;
  status: string;
  due?: string;
}

export const WorkspaceCloudSqlHub: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(getGoogleAccessToken());
  const [activeTab, setActiveTab] = useState<'overview' | 'sheets' | 'meet' | 'forms' | 'tasks' | 'cloudsql'>('overview');

  // Cloud SQL sync state
  const [cloudSqlConnected, setCloudSqlConnected] = useState<boolean>(true);
  const [syncedItems, setSyncedItems] = useState<WorkspaceItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Google Sheets state
  const [sheetTitle, setSheetTitle] = useState('meUus 64-District Strategic Tracker');
  const [createdSheet, setCreatedSheet] = useState<{ spreadsheetId: string; spreadsheetUrl: string; title: string } | null>(null);
  const [creatingSheet, setCreatingSheet] = useState(false);
  const [sheetReadData, setSheetReadData] = useState<any>(null);
  const [readingSheet, setReadingSheet] = useState(false);

  // Google Meet state
  const [meetTopic, setMeetTopic] = useState('meUus Soul Reflection & Community Dialogue');
  const [createdMeeting, setCreatedMeeting] = useState<{ meetingUri: string; meetingCode?: string; topic?: string } | null>(null);
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Google Forms state
  const [formTitle, setFormTitle] = useState('meUus Citizen Empowerment & Growth Survey');
  const [createdForm, setCreatedForm] = useState<{ formId: string; responderUri: string; title: string } | null>(null);
  const [creatingForm, setCreatingForm] = useState(false);

  // Google Tasks state
  const [tasks, setTasks] = useState<GoogleTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskNotes, setNewTaskNotes] = useState('Consistent, Honest Action Over Perfection');
  const [creatingTask, setCreatingTask] = useState(false);

  // Notification message
  const [bannerMsg, setBannerMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoadingAuth(false);
      const token = getGoogleAccessToken();
      setAccessToken(token);

      if (user) {
        try {
          const idToken = await user.getIdToken();
          // Sync with Cloud SQL
          await fetch('/api/user/sync', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            },
          });
          loadCloudSqlItems(idToken);
          if (token) {
            loadGoogleTasks(token);
          }
        } catch (e) {
          console.error('Error syncing user with Cloud SQL:', e);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setBannerMsg(null);
      const { user, token } = await loginWithGoogle();
      setCurrentUser(user);
      setAccessToken(token);
      setBannerMsg({ type: 'success', text: `Welcome, ${user.displayName || user.email}! Connected to Google Workspace & Cloud SQL.` });
      
      const idToken = await user.getIdToken();
      await fetch('/api/user/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      loadCloudSqlItems(idToken);
      if (token) {
        loadGoogleTasks(token);
      }
    } catch (err: any) {
      console.error('Sign-in failed:', err);
      setBannerMsg({ type: 'error', text: err.message || 'Google Sign-In failed. Please try again.' });
    }
  };

  const handleSignOut = async () => {
    await logoutUser();
    setCurrentUser(null);
    setAccessToken(null);
    setSyncedItems([]);
    setTasks([]);
    setBannerMsg({ type: 'info', text: 'You have signed out.' });
  };

  const loadCloudSqlItems = async (idToken: string) => {
    try {
      setLoadingItems(true);
      const res = await fetch('/api/workspace/items', {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSyncedItems(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load items from Cloud SQL:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadGoogleTasks = async (token: string) => {
    try {
      setLoadingTasks(true);
      const res = await fetch('/api/google/tasks/list?tasklist=@default', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load tasks from Google Tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Google Sheets Action
  const handleCreateSheet = async () => {
    if (!accessToken) {
      setBannerMsg({ type: 'error', text: 'Please sign in with Google to create spreadsheets in your Google Drive.' });
      return;
    }
    try {
      setCreatingSheet(true);
      const res = await fetch('/api/google/sheets/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: sheetTitle,
          initialHeaders: ['meUus Ecosystem Hub', 'Action Objective', 'Target Region', 'Status', 'Date']
        })
      });
      const data = await res.json();
      if (res.ok) {
        setCreatedSheet(data);
        setBannerMsg({ type: 'success', text: `Google Sheet "${data.title}" successfully created in your Google Drive!` });
        
        // Save link to Cloud SQL
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          await fetch('/api/workspace/items', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              hubId: 'google-sheets',
              title: data.title,
              content: `Spreadsheet ID: ${data.spreadsheetId}`,
              category: 'sheets',
              externalId: data.spreadsheetId,
              externalUrl: data.spreadsheetUrl,
            })
          });
          loadCloudSqlItems(idToken);
        }
      } else {
        setBannerMsg({ type: 'error', text: data.error || 'Failed to create Google Sheet.' });
      }
    } catch (err: any) {
      setBannerMsg({ type: 'error', text: err.message || 'Error communicating with Google Sheets API' });
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleReadSheet = async () => {
    if (!accessToken || !createdSheet?.spreadsheetId) return;
    try {
      setReadingSheet(true);
      const res = await fetch(`/api/google/sheets/read?spreadsheetId=${createdSheet.spreadsheetId}&range=A1:E10`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const data = await res.json();
      if (res.ok) {
        setSheetReadData(data);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setReadingSheet(false);
    }
  };

  // Google Meet Action
  const handleCreateMeet = async () => {
    if (!accessToken) {
      setBannerMsg({ type: 'error', text: 'Please sign in with Google to create Google Meet spaces.' });
      return;
    }
    try {
      setCreatingMeeting(true);
      const res = await fetch('/api/google/meet/create-space', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ topic: meetTopic })
      });
      const data = await res.json();
      if (res.ok) {
        setCreatedMeeting(data);
        setBannerMsg({ type: 'success', text: 'Google Meet conference space generated successfully!' });

        // Save reference to Cloud SQL
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          await fetch('/api/workspace/items', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              hubId: 'google-meet',
              title: meetTopic,
              content: `Meeting Room Link: ${data.meetingUri}`,
              category: 'meet',
              externalId: data.name,
              externalUrl: data.meetingUri,
            })
          });
          loadCloudSqlItems(idToken);
        }
      }
    } catch (err: any) {
      setBannerMsg({ type: 'error', text: err.message || 'Failed to create Google Meet space' });
    } finally {
      setCreatingMeeting(false);
    }
  };

  // Google Forms Action
  const handleCreateForm = async () => {
    if (!accessToken) {
      setBannerMsg({ type: 'error', text: 'Please sign in with Google to create Google Forms.' });
      return;
    }
    try {
      setCreatingForm(true);
      const res = await fetch('/api/google/forms/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formTitle,
          description: 'A structured community feedback & reflection survey powered by meUus & Google Forms.'
        })
      });
      const data = await res.json();
      if (res.ok) {
        setCreatedForm(data);
        setBannerMsg({ type: 'success', text: `Google Form created! Responder URI: ${data.responderUri}` });

        // Save reference to Cloud SQL
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          await fetch('/api/workspace/items', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              hubId: 'google-forms',
              title: data.title,
              content: `Form ID: ${data.formId}`,
              category: 'forms',
              externalId: data.formId,
              externalUrl: data.responderUri,
            })
          });
          loadCloudSqlItems(idToken);
        }
      } else {
        setBannerMsg({ type: 'error', text: data.error || 'Failed to create Google Form.' });
      }
    } catch (err: any) {
      setBannerMsg({ type: 'error', text: err.message || 'Error communicating with Google Forms API' });
    } finally {
      setCreatingForm(false);
    }
  };

  // Google Tasks Action
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    if (!accessToken) {
      setBannerMsg({ type: 'error', text: 'Please sign in with Google to sync with Google Tasks.' });
      return;
    }

    try {
      setCreatingTask(true);
      const res = await fetch('/api/google/tasks/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTaskTitle,
          notes: newTaskNotes
        })
      });
      const data = await res.json();
      if (res.ok && data.task) {
        setTasks([data.task, ...tasks]);
        setNewTaskTitle('');
        setBannerMsg({ type: 'success', text: `Added "${data.task.title}" directly to your Google Tasks!` });

        // Save reference to Cloud SQL
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          await fetch('/api/workspace/items', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              hubId: 'google-tasks',
              title: data.task.title,
              content: newTaskNotes,
              category: 'tasks',
              externalId: data.task.id,
            })
          });
          loadCloudSqlItems(idToken);
        }
      }
    } catch (err: any) {
      setBannerMsg({ type: 'error', text: err.message || 'Failed to add task.' });
    } finally {
      setCreatingTask(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    if (!accessToken) return;
    try {
      const res = await fetch('/api/google/tasks/complete', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ taskId, status: 'completed' })
      });
      if (res.ok) {
        setTasks(tasks.map(t => t.id === taskId ? { ...t, status: 'completed' } : t));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="w-full bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-8 text-slate-100 shadow-2xl backdrop-blur-xl my-8">
      {/* Header & Auth Ribbon */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5" />
              Cloud SQL (PostgreSQL us-west1)
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-500/20 text-sky-400 border border-sky-500/30 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              Google Workspace Suite
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white flex items-center gap-2">
            meUus Workspace & Cloud SQL Hub
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Seamlessly synchronize your strategic roadmaps, video sessions, civic surveys, and action tasks with Google Workspace and Cloud SQL relational persistence.
          </p>
        </div>

        {/* User Auth Controls */}
        <div className="flex items-center gap-3">
          {currentUser ? (
            <div className="flex items-center gap-3 bg-slate-800/80 border border-slate-700 rounded-2xl p-2 pr-4">
              {currentUser.photoURL ? (
                <img src={currentUser.photoURL} alt={currentUser.displayName || 'User'} className="w-9 h-9 rounded-xl object-cover border border-slate-600" />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold">
                  {(currentUser.displayName || currentUser.email || 'U')[0].toUpperCase()}
                </div>
              )}
              <div className="text-left">
                <div className="text-xs font-bold text-white leading-tight">{currentUser.displayName || 'Authenticated Citizen'}</div>
                <div className="text-[11px] text-slate-400 leading-tight">{currentUser.email}</div>
              </div>
              <button 
                onClick={handleSignOut}
                className="ml-2 p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700/50 rounded-xl transition"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleGoogleSignIn}
              disabled={loadingAuth}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white font-semibold rounded-2xl shadow-lg shadow-indigo-500/20 transition flex items-center gap-2 text-sm"
            >
              <LogIn className="w-4 h-4" />
              Sign in with Google Workspace
            </button>
          )}
        </div>
      </div>

      {/* Banner message */}
      {bannerMsg && (
        <div className={`mt-4 p-3.5 rounded-2xl text-sm flex items-center gap-2 border ${
          bannerMsg.type === 'success' ? 'bg-emerald-950/50 border-emerald-500/40 text-emerald-300' :
          bannerMsg.type === 'error' ? 'bg-rose-950/50 border-rose-500/40 text-rose-300' :
          'bg-slate-800 border-slate-700 text-slate-300'
        }`}>
          {bannerMsg.type === 'success' && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
          {bannerMsg.type === 'error' && <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />}
          <span>{bannerMsg.text}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 pt-6 pb-4 border-b border-slate-800 text-sm">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-xl font-medium transition flex items-center gap-2 ${
            activeTab === 'overview' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <Layers className="w-4 h-4" />
          Full Overview
        </button>
        <button
          onClick={() => setActiveTab('sheets')}
          className={`px-4 py-2 rounded-xl font-medium transition flex items-center gap-2 ${
            activeTab === 'sheets' ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Google Sheets
        </button>
        <button
          onClick={() => setActiveTab('meet')}
          className={`px-4 py-2 rounded-xl font-medium transition flex items-center gap-2 ${
            activeTab === 'meet' ? 'bg-rose-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <Video className="w-4 h-4" />
          Google Meet
        </button>
        <button
          onClick={() => setActiveTab('forms')}
          className={`px-4 py-2 rounded-xl font-medium transition flex items-center gap-2 ${
            activeTab === 'forms' ? 'bg-purple-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <FileText className="w-4 h-4" />
          Google Forms
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          className={`px-4 py-2 rounded-xl font-medium transition flex items-center gap-2 ${
            activeTab === 'tasks' ? 'bg-amber-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <CheckSquare className="w-4 h-4" />
          Google Tasks
        </button>
        <button
          onClick={() => setActiveTab('cloudsql')}
          className={`px-4 py-2 rounded-xl font-medium transition flex items-center gap-2 ${
            activeTab === 'cloudsql' ? 'bg-sky-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <Database className="w-4 h-4" />
          Cloud SQL Records ({syncedItems.length})
        </button>
      </div>

      {/* Tab Contents */}
      <div className="pt-6">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Sheets Card */}
            <div className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-5 hover:border-emerald-500/50 transition">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-white text-base">Google Sheets</h3>
              <p className="text-slate-400 text-xs mt-1 mb-4 leading-relaxed">
                Export 64-district initiatives, metrics, and business models straight into live Google Sheets.
              </p>
              <button
                onClick={() => setActiveTab('sheets')}
                className="w-full py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 font-semibold rounded-xl text-xs border border-emerald-500/30 transition"
              >
                Open Sheets Tools →
              </button>
            </div>

            {/* Meet Card */}
            <div className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-5 hover:border-rose-500/50 transition">
              <div className="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center mb-3">
                <Video className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-white text-base">Google Meet</h3>
              <p className="text-slate-400 text-xs mt-1 mb-4 leading-relaxed">
                Launch instant video rooms for meUus Soul dialogue, legal advice, or youth entrepreneurship circles.
              </p>
              <button
                onClick={() => setActiveTab('meet')}
                className="w-full py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 font-semibold rounded-xl text-xs border border-rose-500/30 transition"
              >
                Create Meet Room →
              </button>
            </div>

            {/* Forms Card */}
            <div className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-5 hover:border-purple-500/50 transition">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center mb-3">
                <FileText className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-white text-base">Google Forms</h3>
              <p className="text-slate-400 text-xs mt-1 mb-4 leading-relaxed">
                Publish civic reflection questionnaires and skill assessments directly to Google Forms.
              </p>
              <button
                onClick={() => setActiveTab('forms')}
                className="w-full py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 font-semibold rounded-xl text-xs border border-purple-500/30 transition"
              >
                Create Form →
              </button>
            </div>

            {/* Tasks Card */}
            <div className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-5 hover:border-amber-500/50 transition">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center mb-3">
                <CheckSquare className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-white text-base">Google Tasks</h3>
              <p className="text-slate-400 text-xs mt-1 mb-4 leading-relaxed">
                Turn meUus action items into Google Tasks synced across all mobile and desktop devices.
              </p>
              <button
                onClick={() => setActiveTab('tasks')}
                className="w-full py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-semibold rounded-xl text-xs border border-amber-500/30 transition"
              >
                Manage Tasks →
              </button>
            </div>
          </div>
        )}

        {/* SHEETS TAB */}
        {activeTab === 'sheets' && (
          <div className="space-y-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                Create New meUus Spreadsheet
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Generates a live, fully-structured Google Sheet in your personal Google Drive with custom columns for ecosystem tracking.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={sheetTitle}
                  onChange={(e) => setSheetTitle(e.target.value)}
                  placeholder="Spreadsheet Title..."
                  className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500"
                />
                <button
                  onClick={handleCreateSheet}
                  disabled={creatingSheet}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20"
                >
                  {creatingSheet ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {creatingSheet ? 'Creating Sheet...' : 'Create in Google Drive'}
                </button>
              </div>

              {createdSheet && (
                <div className="mt-6 p-4 bg-emerald-950/30 border border-emerald-500/30 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Spreadsheet Ready</div>
                    <div className="text-sm font-semibold text-white mt-0.5">{createdSheet.title}</div>
                    <div className="text-xs text-slate-400">ID: {createdSheet.spreadsheetId}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleReadSheet}
                      disabled={readingSheet}
                      className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-lg border border-slate-600 transition"
                    >
                      {readingSheet ? 'Reading...' : 'Preview Data'}
                    </button>
                    <a
                      href={createdSheet.spreadsheetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    >
                      Open in Google Sheets
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}

              {sheetReadData && (
                <div className="mt-4 p-4 bg-slate-900 border border-slate-800 rounded-xl text-xs font-mono overflow-x-auto">
                  <div className="text-slate-400 mb-2 font-sans font-semibold">Live Google Sheets Values:</div>
                  <pre>{JSON.stringify(sheetReadData.values || [], null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MEET TAB */}
        {activeTab === 'meet' && (
          <div className="space-y-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <Video className="w-5 h-5 text-rose-400" />
                Generate Google Meet Conference Room
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Provision a Google Meet conference space via Google Meet REST API for instant dialogue, therapy, legal aid, or collaborative brainstorming.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={meetTopic}
                  onChange={(e) => setMeetTopic(e.target.value)}
                  placeholder="Meeting Agenda or Session Name..."
                  className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-rose-500"
                />
                <button
                  onClick={handleCreateMeet}
                  disabled={creatingMeeting}
                  className="px-6 py-2.5 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-xl text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-rose-600/20"
                >
                  {creatingMeeting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
                  {creatingMeeting ? 'Creating Space...' : 'Create Instant Meet'}
                </button>
              </div>

              {createdMeeting && (
                <div className="mt-6 p-5 bg-rose-950/30 border border-rose-500/30 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="text-xs text-rose-400 font-bold uppercase tracking-wider">Active Meeting Space</div>
                    <div className="text-base font-bold text-white mt-0.5">{meetTopic}</div>
                    <div className="text-xs text-slate-300 font-mono mt-1">{createdMeeting.meetingUri}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdMeeting.meetingUri);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      }}
                      className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-600 transition flex items-center gap-1.5"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                    <a
                      href={createdMeeting.meetingUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-xl transition flex items-center gap-1.5 shadow-lg shadow-rose-600/30"
                    >
                      Join Meeting Now
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* FORMS TAB */}
        {activeTab === 'forms' && (
          <div className="space-y-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Google Forms Creator & Survey Publisher
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Build and deploy surveys with automatic questions mapped to meUus 9 Solution Hubs and 3-day action steps.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Form Title..."
                  className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-purple-500"
                />
                <button
                  onClick={handleCreateForm}
                  disabled={creatingForm}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-xl text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20"
                >
                  {creatingForm ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {creatingForm ? 'Creating Form...' : 'Publish Google Form'}
                </button>
              </div>

              {createdForm && (
                <div className="mt-6 p-5 bg-purple-950/30 border border-purple-500/30 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="text-xs text-purple-400 font-bold uppercase tracking-wider">Form Published</div>
                    <div className="text-base font-bold text-white mt-0.5">{createdForm.title}</div>
                    <div className="text-xs text-slate-400 mt-1">Form ID: {createdForm.formId}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={createdForm.responderUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold rounded-xl transition flex items-center gap-1.5 shadow-lg shadow-purple-600/30"
                    >
                      Fill Survey / Share Link
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TASKS TAB */}
        {activeTab === 'tasks' && (
          <div className="space-y-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <CheckSquare className="w-5 h-5 text-amber-400" />
                Google Tasks Bi-directional Synchronization
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Add actionable commitments to your Google Tasks directly from meUus, staying true to "Consistent, Honest Action Over Perfection".
              </p>

              <form onSubmit={handleCreateTask} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="New Task (e.g. Implement Idea Lab Prototype)..."
                  className="sm:col-span-2 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500"
                />
                <button
                  type="submit"
                  disabled={creatingTask || !newTaskTitle.trim()}
                  className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-xl text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20 disabled:opacity-50"
                >
                  {creatingTask ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add to Google Tasks
                </button>
              </form>

              {/* Tasks List */}
              <div className="space-y-2">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>Your Live Google Tasks ({tasks.length})</span>
                  {accessToken && (
                    <button 
                      onClick={() => loadGoogleTasks(accessToken)} 
                      disabled={loadingTasks}
                      className="text-amber-400 hover:underline text-[11px] flex items-center gap-1"
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingTasks ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                  )}
                </div>

                {tasks.length === 0 ? (
                  <div className="p-6 bg-slate-900/50 border border-slate-800 rounded-xl text-center text-slate-400 text-sm">
                    {accessToken ? 'No tasks found in your primary Google Tasks list.' : 'Sign in to fetch your Google Tasks.'}
                  </div>
                ) : (
                  tasks.map((task) => (
                    <div
                      key={task.id}
                      className="p-3.5 bg-slate-900/70 border border-slate-800 hover:border-slate-700 rounded-xl flex items-center justify-between gap-3 transition"
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleCompleteTask(task.id)}
                          disabled={task.status === 'completed'}
                          className={`w-5 h-5 rounded-lg border flex items-center justify-center transition ${
                            task.status === 'completed'
                              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                              : 'border-slate-600 hover:border-amber-400 text-transparent'
                          }`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                        <div>
                          <div className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-slate-500' : 'text-slate-200'}`}>
                            {task.title}
                          </div>
                          {task.notes && (
                            <div className="text-xs text-slate-400 mt-0.5">{task.notes}</div>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        task.status === 'completed' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-amber-950 text-amber-400 border border-amber-800'
                      }`}>
                        {task.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* CLOUD SQL TAB */}
        {activeTab === 'cloudsql' && (
          <div className="space-y-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Database className="w-5 h-5 text-sky-400" />
                    Cloud SQL PostgreSQL Relational Storage
                  </h3>
                  <p className="text-slate-400 text-xs mt-1">
                    Managed instance: <code className="text-sky-300 bg-sky-950/60 px-2 py-0.5 rounded">ai-studio-1f6d8234</code> in region <code className="text-sky-300 bg-sky-950/60 px-2 py-0.5 rounded">us-west1</code>
                  </p>
                </div>
                <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-full text-xs font-semibold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Active & Healthy
                </span>
              </div>

              {/* Synced items list */}
              <div className="space-y-3">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  PostgreSQL Workspace Items Table ({syncedItems.length} records)
                </div>

                {syncedItems.length === 0 ? (
                  <div className="p-6 bg-slate-900/50 border border-slate-800 rounded-xl text-center text-slate-400 text-sm">
                    No items stored yet. Create a Sheet, Meet space, Form, or Task to log items in Cloud SQL!
                  </div>
                ) : (
                  syncedItems.map((item) => (
                    <div
                      key={item.id}
                      className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-800 text-sky-400 border border-slate-700">
                            {item.hubId}
                          </span>
                          <span className="text-sm font-semibold text-white">{item.title}</span>
                        </div>
                        {item.content && (
                          <div className="text-xs text-slate-400 mt-1">{item.content}</div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-1 font-mono">
                          Logged: {new Date(item.createdAt).toLocaleString()}
                        </div>
                      </div>

                      {item.externalUrl && (
                        <a
                          href={item.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-sky-300 text-xs font-semibold rounded-lg border border-slate-700 transition flex items-center gap-1.5 self-start sm:self-center"
                        >
                          View External Resource
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
